import { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Alert, List, ListItem, ListItemText, Button, Box } from '@mui/material';
import { useMaterialReactTable, MaterialReactTable } from 'material-react-table';
import { AUTH_LEVELS } from '../../auth/Authorized';
import {
  tableSelectionOptions,
  defaultTableOptions,
  tableCellularEditingOptions,
  defaultTableInitialState,
} from '../../util/data-table';
import getBuildingStaging from '../../../redux/selectors/staging';
import { updateStagingPoints, unpublishStagingAction } from '../../../redux/actions/staging';
import {
  RECENTLY_DISCOVERED_MS,
  RECENTLY_DISCOVERED_DAYS,
  pointColumns,
  pointSuperColumn,
  pointNameId,
  pointTypeId,
  pointTypeConfidenceId,
  pointDescriptionId,
  pointPresentValueId,
  pointUnitsId,
  pointRawUnitId,
  pointEquipNamesId,
  lastPublishedCol,
} from '../columns';
import { groupByProperty, unique } from '../../../utils/collections';
import { stagingPointDiff } from '../staging';
import PointTypeSelector from '../selectors/PointTypeSelector';
import UnitSelector from '../selectors/UnitSelector';
import EnumSelectorDialog from '../dialogs/EnumSelectorDialog';
import ConfirmDialog from '../dialogs/ConfirmDialog';
import EquipmentSelectorDialog from '../dialogs/EquipmentSelectorDialog';
import { unpublishConfirm } from '../dialogs/confirmations';

const Points = ({
  sorting,
  onSortingChange,
  columnFilters,
  onColumnFiltersChange,

  pointsLoadTime,
  stagingPoints,
  stagingEquipment,
  canWrite,
  pointTypes,
  units,
  equipTypes,
  lastDiscovery,

  stagingPointUpdates,
  unpublish,
}) => {
  const [confirmation, setConfirmation] = useState({});
  const [pointUpdatePointTypes, setPointUpdatePointTypes] = useState([]);
  const [pointUpdateUnits, setPointUpdateUnits] = useState([]);
  const [pointUpdateAssignments, setPointUpdateAssignments] = useState([]);

  const equipNames = useMemo(
    () => unique(stagingPoints.flatMap(({ equip }) => equip).map(({ name }) => name)),
    [stagingPoints]
  );
  const columns = useMemo(
    () =>
      pointColumns(
        canWrite,
        pointTypes,
        units,
        equipNames,
        equipTypes,
        pointSuperColumn,
        stagingPointUpdates
      ),
    [canWrite, equipNames, equipTypes, pointTypes, units, stagingPointUpdates]
  );

  const data = useMemo(
    () =>
      stagingPoints.map((p) => ({
        ...p,
        diff: stagingPointDiff(p, null),
      })),
    [stagingPoints]
  );

  const columnIds = useMemo(() => columns.map(({ id }) => id), [columns]);
  const pointsSorting = useMemo(
    () => sorting.filter(({ id }) => columnIds.includes(id)),
    [sorting, columnIds]
  );
  const pointsColumnFilters = useMemo(
    () => columnFilters.filter(({ id }) => columnIds.includes(id)),
    [columnFilters, columnIds]
  );

  const mrt = useMaterialReactTable({
    ...defaultTableOptions,
    ...tableSelectionOptions,
    ...tableCellularEditingOptions,
    columns,
    data,
    initialState: {
      ...defaultTableInitialState({
        columnFilters: pointsColumnFilters,
        sorted: pointsSorting,
        hiddenColumns: columns
          .map((c) => c.id)
          .filter(
            (id) =>
              ![
                pointSuperColumn.id,
                pointNameId,
                pointTypeId,
                pointTypeConfidenceId,
                pointDescriptionId,
                pointPresentValueId,
                pointUnitsId,
                pointRawUnitId,
                pointEquipNamesId,
                lastPublishedCol.id,
              ].includes(id)
          ),
        forcedHiddenColumns: pointSuperColumn.subColumns.map(({ id }) => id),
      }),
    },
    getRowId: (p) => p.topic,
    // sorting
    onSortingChange,
    // filtering
    onColumnFiltersChange,
    state: {
      sorting: pointsSorting,
      columnFilters: pointsColumnFilters,
      showAlertBanner: true,
      isLoading: !pointsLoadTime,
    },
    enableEditing: canWrite,
    renderTopToolbarCustomActions: ({ table }) => {
      const handlePointType = () => {
        setPointUpdatePointTypes(
          table.getSelectedRowModel().flatRows.map(({ original }) => original)
        );
      };

      const handleUnit = () => {
        setPointUpdateUnits(table.getSelectedRowModel().flatRows.map(({ original }) => original));
      };

      const handleAdd = () => {
        setPointUpdateAssignments(
          table.getSelectedRowModel().flatRows.map(({ original }) => original)
        );
      };

      return (
        <Box sx={{ display: 'flex', gap: '0.5rem' }}>
          <Button
            color="success"
            disabled={table.getSelectedRowModel().flatRows.length <= 0}
            onClick={handlePointType}
            variant="contained"
          >
            Update Point Type
          </Button>
          <Button
            color="success"
            disabled={table.getSelectedRowModel().flatRows.length <= 0}
            onClick={handleUnit}
            variant="contained"
          >
            Update Unit
          </Button>
          <Button
            color="success"
            disabled={table.getSelectedRowModel().flatRows.length <= 0}
            onClick={handleAdd}
            variant="contained"
          >
            Add To Equipment
          </Button>
          <Button
            color="success"
            disabled={
              table.getSelectedRowModel().flatRows.length <= 0 ||
              !table
                .getSelectedRowModel()
                .flatRows?.every(
                  ({ original }) =>
                    original.device.deviceEquipment &&
                    !original.equipIds.includes(original.device.deviceEquipment?.equipId)
                )
            }
            onClick={() => {
              const selection = table
                .getSelectedRowModel()
                .flatRows.map(({ original }) => original);
              const impactedPoints = selection.filter(
                (p) => !p.equipIds.includes(p.device.deviceEquipment.equipId)
              );
              const impactedEquipment = unique(
                impactedPoints.map((p) => p.device.deviceEquipment),
                (e) => e.equipId
              );
              return setConfirmation({
                id: 'restoration',
                onExit: () => setConfirmation({}),
                content: (
                  <>
                    The following equipment will have points restored:
                    <List sx={{ listStyleType: 'disc' }}>
                      {impactedEquipment.map((e) => (
                        <ListItem key={e.equipId} sx={{ display: 'list-item', padding: 0 }}>
                          <ListItemText>{e.name}</ListItemText>
                        </ListItem>
                      ))}
                    </List>
                  </>
                ),
                onSuccess: () =>
                  stagingPointUpdates(
                    selection.map((p) => ({
                      topic: p.topic,
                      equip_ids: p.equipIds.includes(p.device.deviceEquipment.equipId)
                        ? p.equipIds
                        : p.equipIds.concat(p.device.deviceEquipment.equipId),
                    }))
                  ),
              });
            }}
            variant="contained"
          >
            Restore Equipment Assignment
          </Button>
          <Button
            color="error"
            disabled={
              table.getSelectedRowModel().flatRows.length <= 0 ||
              !table
                .getSelectedRowModel()
                .flatRows?.every(({ original }) => original.equipIds.length > 0)
            }
            onClick={() => {
              const publishedPointsImpacted = table
                .getSelectedRowModel()
                .flatRows.map(({ original }) => original)
                .filter(({ published }) => Boolean(published));
              const impactedEquipIds = publishedPointsImpacted.flatMap(({ equipIds }) => equipIds);
              const publishedEquipmentImpacted = Object.values(
                groupByProperty(
                  'equipId',
                  publishedPointsImpacted.flatMap((point) =>
                    point.equip.filter(({ equipId }) => impactedEquipIds.includes(equipId))
                  )
                )
              );
              function finishRemoval() {
                stagingPointUpdates(
                  table
                    .getSelectedRowModel()
                    .flatRows.map(({ original }) => ({ topic: original.topic, equip_ids: [] }))
                );
              }
              if (publishedEquipmentImpacted.length > 0)
                return setConfirmation({
                  ...unpublishConfirm(
                    unpublish,
                    [],
                    publishedPointsImpacted.map((p) => p.publishedPointId),
                    publishedPointsImpacted.flatMap((p) =>
                      p.equip
                        .filter((e) => e.publishedEquipmentId)
                        .map((e) => ({
                          equipment_id: e.publishedEquipmentId,
                          point_id: p.publishedPointId,
                        }))
                    ),
                    (cont) => (cont ? finishRemoval() : null)
                  ),
                  onExit: () => setConfirmation({}),
                });
              return finishRemoval();
            }}
            variant="contained"
          >
            Remove From Equipment
          </Button>
          <Button
            color="error"
            disabled={
              table.getSelectedRowModel().flatRows.length <= 0 ||
              !table.getSelectedRowModel().flatRows?.every(({ original }) => original.published)
            }
            onClick={() =>
              setConfirmation({
                id: 'reset',
                content: `This will revert ${table.getSelectedRowModel().flatRows.length} points to their state at the time of last publish. This includes equipment assignment, point type, and units.`,
                onExit: () => setConfirmation({}),
                onSuccess: () =>
                  stagingPointUpdates(
                    table
                      .getSelectedRowModel()
                      .flatRows.map(({ original }) => original)
                      .filter(({ published }) => Boolean(published))
                      .map((p) => ({
                        topic: p.topic,
                        point_type: { id: p.published.pointTypeId, confidence: 100 },
                        raw_unit: { id: p.published.rawUnitId, confidence: 100 },
                        equip_ids: p.equip
                          .filter((e) => e.published)
                          .filter((e) =>
                            e.published.points.some((published) => published.topic === p.topic)
                          )
                          .map((e) => e.equipId),
                      }))
                  ),
              })
            }
            variant="contained"
          >
            Reset to Last Published
          </Button>
        </Box>
      );
    },
  });

  return (
    <>
      {stagingPoints.some(
        (p) => !p.data['auto_tagger.point_type_preds'] && p.device.protocol === 'bacnet'
      ) ? (
        <Alert severity="warning">
          Some confidence values are not yet available and will appear after our processing is
          complete
        </Alert>
      ) : null}
      {lastDiscovery && new Date() - lastDiscovery > RECENTLY_DISCOVERED_MS ? (
        <Alert severity="warning">
          At least one bacnet collection has not been discovered in the last{' '}
          {RECENTLY_DISCOVERED_DAYS} days
        </Alert>
      ) : null}
      <EnumSelectorDialog
        onSubmit={(pt) =>
          stagingPointUpdates(
            pointUpdatePointTypes.map((p) => ({
              topic: p.topic,
              point_type: { id: pt.id, confidence: 100 },
            }))
          ).then(() => setPointUpdatePointTypes([]))
        }
        onCancel={() => setPointUpdatePointTypes([])}
        child={PointTypeSelector}
        entities={pointUpdatePointTypes}
      />
      <EnumSelectorDialog
        onSubmit={(u) =>
          stagingPointUpdates(
            pointUpdateUnits.map((p) => ({
              topic: p.topic,
              raw_unit: { id: u.id, confidence: 100 },
            }))
          ).then(() => setPointUpdateUnits([]))
        }
        onCancel={() => setPointUpdateUnits([])}
        child={UnitSelector}
        entities={pointUpdateUnits}
      />
      <EquipmentSelectorDialog
        onSubmit={(equipId) =>
          stagingPointUpdates(
            pointUpdateAssignments.map((p) => ({
              topic: p.topic,
              equip_ids: p.equipIds.includes(equipId) ? p.equipIds : p.equipIds.concat(equipId),
            }))
          ).then(() => setPointUpdateAssignments([]))
        }
        onCancel={() => setPointUpdateAssignments([])}
        open={pointUpdateAssignments.length > 0}
        equipment={stagingEquipment}
      />
      <ConfirmDialog
        id={confirmation.id}
        content={confirmation.content}
        onSuccess={confirmation.onSuccess}
        onExit={confirmation.onExit}
      />
      <MaterialReactTable table={mrt} />
    </>
  );
};

const mapStateToProps = (state, { building }) => {
  const {
    user: { userAuthLevel },
    entities: {
      staging: { pointsLoadTime },
      equipTypes,
      pointTypes,
      units,
      deployments: { deployments, discovery },
    },
  } = state;
  const { stagingPoints, stagingEquipment } = getBuildingStaging(state, building.id);
  const lastDiscovery = Math.min(
    ...Object.values(deployments)
      .filter((d) => d.buildings.includes(building.id))
      .flatMap((d) => discovery[d.site])
      .filter(Boolean)
      .map((disc) => disc.last_status_update)
  );
  return {
    pointsLoadTime: pointsLoadTime[building.id] ?? false,
    stagingPoints: [...stagingPoints.values()],
    stagingEquipment: [...stagingEquipment.values()],
    pointTypes: pointTypes.byId,
    units: units.byId,
    equipTypes: equipTypes.byId,
    isSuperAdmin: userAuthLevel === AUTH_LEVELS.SUPER_ADMIN,
    canWrite: userAuthLevel < AUTH_LEVELS.ORG_USER,
    lastDiscovery: lastDiscovery === Infinity ? null : new Date(lastDiscovery),
  };
};

const mapDispatchToProps = (dispatch, { building }) => ({
  stagingPointUpdates: (pointUpdates) => dispatch(updateStagingPoints(building.id, pointUpdates)),
  unpublish: (equipmentIds, pointIds, links) =>
    dispatch(unpublishStagingAction(building.id, equipmentIds, pointIds, links)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Points);
